import {
  customElement,
  FASTElement,
  Observable,
  observable,
  Splice,
} from '@microsoft/fast-element';
import { QuadrantSpiderChartTemplate as template } from './quadrant-spider-chart.template';
import { QuadrantSpiderChartStyles as styles } from './quadrant-spider-chart.styles';
import { IBuyerAnalyticsService } from '../../../../../services';
import { VppaRankings } from '../../../../../types';
import Highcharts, { Chart } from 'highcharts';
import more from 'highcharts/highcharts-more';
import { PRODUCT_TYPE } from '../../../../../utils';
import { getFormattedNumber, getPriceValue } from '../../../../../utils/formatting';

more(Highcharts);

const name = 'quadrant-spider-chart';

@customElement({
  name,
  template,
  styles,
})
export class QuadrantSpiderChart extends FASTElement {
  @IBuyerAnalyticsService buyerAnalyticsService: IBuyerAnalyticsService;
  @observable selectedVppaRankings: VppaRankings[] = [];
  @observable quadrantSpiderChart!: HTMLDivElement;
  @observable chart: Chart;

  private notifier1: any;
  private handler1: any;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadVppaRankings();
    this.renderSpiderChart();

    window.addEventListener('resize', () => {
      this.reflow();
    });
    setTimeout(() => {
      if (this.buyerAnalyticsService.selectedVppas) {
        this.selectedVppaRankings = this.buyerAnalyticsService.selectedVppas;
        Observable.notify(this.buyerAnalyticsService, 'selectedVppas');
      }
    }, 100);
  }

  private loadVppaRankings() {
    const context = this;
    this.notifier1 = Observable.getNotifier(this.buyerAnalyticsService);
    this.handler1 = {
      handleChange(source: any, splices: Splice[]) {
        context.selectedVppaRankings = source.selectedVppas;
      },
    };

    this.notifier1.subscribe(this.handler1, 'selectedVppas');
  }

  private renderSpiderChart() {
    let data = this.getValuesFromData();
    const dataMap = new Map();
    this.selectedVppaRankings.forEach((vppaRanking) =>
      dataMap.set(vppaRanking.TICKER, {
        'Location(ISO)': vppaRanking.ISO,
        'Expected Settlement': getPriceValue(vppaRanking.REFERENCED_EXPECTED_SETTLEMENT, '', '$'),
        LME: getFormattedNumber(vppaRanking.REFERENCED_LME, '', 2),
        'Implied Carbon Price': getPriceValue(vppaRanking.REFERENCED_IMPLIED_CARBON_PRICE, '', '$'),
        'Project Additionality': vppaRanking.PROJECT_OPERATIONAL_STATUS,
      }),
    );
    const vppaRankings = this.selectedVppaRankings;
    let categories = [
      'Location(ISO)',
      'Expected Settlement',
      'LME',
      'Implied Carbon Price',
      'Project Additionality',
    ];
    let maxTitles = 5;

    // If there are no VPPAs selected show an empty spider graph
    if (!data.length) {
      data = [
        {
          name: '',
          type: 'line',
          data: [1],
          pointPlacement: 'on',
          visible: false,
        },
      ];
    }

    if (this.buyerAnalyticsService.productType === PRODUCT_TYPE.RECS) {
      categories = ['Location(ISO)', 'LME', 'Implied Carbon Price'];
      maxTitles = 3;
    }

    if (this.quadrantSpiderChart) {
      setTimeout(() => {
        this.chart = Highcharts.chart({
          chart: {
            renderTo: this.quadrantSpiderChart,
            polar: true,
            type: 'line',
            animation: false,
          },
          title: {
            text: '',
          },
          pane: {
            size: '70%',
          },
          xAxis: {
            allowDecimals: true,
            categories: categories,
            tickmarkPlacement: 'on',
            lineWidth: 0,
            min: 0,
            max: maxTitles,
          },
          yAxis: {
            allowDecimals: true,
            gridLineInterpolation: 'polygon',
            lineWidth: 0,
            min: 0,
          },
          tooltip: {
            shared: true,
            formatter: function () {
              var tooltip = this.x.toString();
              this.points?.forEach((element) => {
                var colorMarker = `<span style="color: ${element.color};">&#9679;</span>`;
                tooltip +=
                  '<br>' +
                  colorMarker +
                  ` ${element.series.name} : ${
                    dataMap.get(element.series.name)[this.x.toString()]
                  }`;
              });

              return tooltip;
            },
          },
          legend: {
            enabled: false,
          },
          plotOptions: {
            line: {
              marker: {
                enabled: true,
              },
            },
            series: {
              animation: false,
            },
          },
          series: data,
          responsive: {
            rules: [
              {
                condition: {
                  maxWidth: 500,
                },
                chartOptions: {
                  legend: {
                    align: 'center',
                    verticalAlign: 'bottom',
                    layout: 'horizontal',
                  },
                  pane: {
                    size: '70%',
                  },
                },
              },
            ],
          },
        });
      }, 0);
    }
  }

  private getValuesFromData() {
    let data: Array<Highcharts.SeriesOptionsType> = [];
    if (this.selectedVppaRankings.length) {
      data = this.selectedVppaRankings.map((rankings, index) => {
        const filteredData =
          (this.buyerAnalyticsService.currentPreferences?.PRODUCT_TYPE ?? '') === PRODUCT_TYPE.RECS
            ? [+rankings.LOCATION, +rankings.LME, +rankings.IMPLIED_CARBON_PRICE]
            : [
                +rankings.LOCATION,
                +rankings.EXPECTED_SETTLEMENT,
                +rankings.LME,
                +rankings.IMPLIED_CARBON_PRICE,
                +rankings.PROJECT_ADDITIONALITY,
              ];

        return {
          name: rankings.TICKER,
          type: 'line',
          data: filteredData,
          color: Highcharts.getOptions().colors[index],
          pointPlacement: 'on',
        };
      });
    }

    return data;
  }

  public reflow() {
    if (this.chart) {
      this.renderSpiderChart();
    }
  }

  public selectedVppaRankingsChanged() {
    this.renderSpiderChart();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.notifier1.unsubscribe(this.handler1);
  }
}
